/**
 * useRecentItems — a tiny localStorage-backed "recently opened" list for the
 * universal command palette (#1515, EPIC #1512).
 *
 * Tracks the destinations a user opens from the palette so the empty state can
 * offer a one-tap return path. Items are deduplicated by ``url`` (most-recent
 * wins), capped at ``MAX_RECENTS`` (newest first) and persisted across reloads.
 *
 * PER-USER SCOPING (#1950): on a SHARED browser, one signed-in user must never
 * see another user's recents. When a ``userId`` is supplied (from the auth
 * context) the storage key is namespaced ``crm-search-recents:<userId>`` so each
 * account keeps its own private history, and switching accounts (login/logout)
 * re-reads the new user's slice. The legacy un-namespaced key is NEVER read for
 * a namespaced user — old global data can't leak across accounts — and is
 * opportunistically purged on first namespaced write. A blank/absent ``userId``
 * (SSR, pre-auth, tests) falls back to the legacy global key, preserving the
 * original behaviour.
 *
 * Storage is guarded for SSR / jsdom (window / localStorage may be absent or
 * throw in a sandboxed test) — every read/write is wrapped so a failure
 * degrades to an in-memory list rather than crashing the palette.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** The legacy, un-namespaced key used before per-user scoping (#1950). */
export const LEGACY_STORAGE_KEY = "crm-search-recents";
export const MAX_RECENTS = 8;

/**
 * Resolve the localStorage key for a given user.
 *
 * A non-blank ``userId`` → a per-user namespaced key; otherwise the legacy
 * global key (SSR / pre-auth / tests).
 */
export function storageKeyFor(userId?: number | string | null): string {
  const id =
    userId === undefined || userId === null ? "" : String(userId).trim();
  return id.length > 0 ? `${LEGACY_STORAGE_KEY}:${id}` : LEGACY_STORAGE_KEY;
}

/** A minimal navigable record — mirrors the palette's row shape. */
export interface RecentItem {
  /** Stable id within its type (search id or registry command id). */
  id: number | string;
  /** Entity/command type, used only for the leading icon. */
  type: string;
  label: string;
  sublabel: string;
  /** SPA-relative URL — also the dedup key. */
  url: string;
}

function hasStorage(): boolean {
  return typeof window !== "undefined" && !!window.localStorage;
}

function read(key: string): RecentItem[] {
  if (!hasStorage()) return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive: keep only well-formed rows.
    return parsed
      .filter(
        (r): r is RecentItem =>
          r &&
          typeof r === "object" &&
          typeof r.url === "string" &&
          typeof r.label === "string",
      )
      .slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

function write(key: string, items: RecentItem[]): void {
  if (!hasStorage()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // Quota / disabled storage — silently degrade to in-memory only.
  }
}

function removeKey(key: string): void {
  if (!hasStorage()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore — best-effort cleanup.
  }
}

export interface RecentItemsApi {
  recents: RecentItem[];
  /** Record an opened item (dedup by url, newest first, capped). */
  add: (item: RecentItem) => void;
  /** Clear the current user's list. */
  clear: () => void;
}

/**
 * @param userId the CURRENT authenticated user's id; namespaces storage so a
 *   shared browser never leaks recents across accounts (#1950). Omit (or pass
 *   null/blank) for the legacy global behaviour.
 */
export function useRecentItems(
  userId?: number | string | null,
): RecentItemsApi {
  const key = storageKeyFor(userId);
  const keyRef = useRef(key);
  const [recents, setRecents] = useState<RecentItem[]>(() => read(key));

  // When the active user changes (login → logout → another login on a SHARED
  // browser) re-read the new user's private slice so the previous account's
  // recents never bleed through. Also purge the legacy global key the first
  // time we operate under a namespaced key, so old un-scoped data can't leak.
  useEffect(() => {
    if (keyRef.current !== key) {
      keyRef.current = key;
      setRecents(read(key));
    }
    if (key !== LEGACY_STORAGE_KEY) {
      removeKey(LEGACY_STORAGE_KEY);
    }
  }, [key]);

  const add = useCallback((item: RecentItem) => {
    if (!item || typeof item.url !== "string" || item.url.length === 0) return;
    setRecents((prev) => {
      const next = [item, ...prev.filter((r) => r.url !== item.url)].slice(
        0,
        MAX_RECENTS,
      );
      write(keyRef.current, next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setRecents([]);
    write(keyRef.current, []);
  }, []);

  return { recents, add, clear };
}
